import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BRAND_NAVY = '#364A98';
const BRAND_ORANGE = '#E76E1C';

function navLinkClass(active) {
  return active
    ? 'text-base font-semibold text-slate-900'
    : 'text-base font-medium text-slate-600 hover:text-slate-900';
}

function mobileNavLinkClass(active) {
  return active
    ? 'flex min-h-12 items-center rounded-xl bg-slate-100 px-4 text-base font-semibold text-slate-900'
    : 'flex min-h-12 items-center rounded-xl px-4 text-base font-medium text-slate-700 hover:bg-slate-50';
}

export default function StoreNav({ showContactOnDesktop = false }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const menuId = useId();
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const createHref = user ? '/dashboard' : '/login?redirect=%2Fdashboard';

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const openMobile = useCallback(() => {
    setMobileOpen(true);
  }, []);

  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  useEffect(() => {
    if (!mobileOpen) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMobile();
        toggleRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    const focusTimer = window.setTimeout(() => {
      const firstLink = menuRef.current?.querySelector('a, button');
      firstLink?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(focusTimer);
    };
  }, [mobileOpen, closeMobile]);

  const handleLogout = () => {
    logout();
    closeMobile();
    navigate('/');
  };

  const isCreateActive = location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/documents');
  const isTemplatesActive = location.pathname.startsWith('/templates');
  const isContactActive = location.pathname.startsWith('/contact');
  const isLibraryActive = location.pathname.startsWith('/my-library');
  const isDashboardActive = location.pathname === '/dashboard';

  return (
    <header className="relative z-50 border-b border-slate-200 bg-white">
      <div className="mx-auto grid max-w-[1400px] grid-cols-12 items-center gap-6 px-6 py-4">
        <Link to="/" className="col-span-7 flex items-center gap-3 md:col-span-4" onClick={closeMobile}>
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
            <img src="/logo.png" alt="DocMania logo" className="h-9 w-9 object-contain" />
          </div>
          <span className="text-[1.9rem] font-extrabold tracking-tight text-slate-900 md:text-[2rem]">
            <span className="text-blue-600">Doc</span>
            <span className="text-orange-500">Mania</span>
          </span>
        </Link>

        <nav className="col-span-5 hidden items-center justify-end gap-7 md:col-span-8 md:flex" aria-label="Main">
          <Link to={createHref} className={navLinkClass(isCreateActive)}>
            Create Documents
          </Link>
          <Link
            to="/templates"
            className={navLinkClass(isTemplatesActive)}
            data-testid="nav-templates-toolkits"
          >
            Templates &amp; Toolkits
          </Link>
          {showContactOnDesktop && (
            <Link to="/contact" className={navLinkClass(isContactActive)} data-testid="header-contact-link">
              Contact
            </Link>
          )}
          {user ? (
            <>
              <Link to="/my-library" className={navLinkClass(isLibraryActive)}>
                My Library
              </Link>
              <Link to="/dashboard" className="text-base font-medium text-slate-600 hover:text-slate-900">
                Dashboard
              </Link>
            </>
          ) : (
            <>
              <Link to="/login" className="text-base font-medium text-slate-600 hover:text-slate-900">
                Login
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center rounded-2xl bg-orange-500 px-5 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-orange-600"
              >
                Get Started
              </Link>
            </>
          )}
        </nav>

        <div className="col-span-5 flex justify-end md:hidden">
          <button
            ref={toggleRef}
            type="button"
            className="inline-flex h-12 w-12 items-center justify-center rounded-xl ring-1 ring-slate-200 transition hover:bg-slate-50"
            onClick={() => (mobileOpen ? closeMobile() : openMobile())}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls={menuId}
            data-testid="mobile-site-nav-toggle"
            style={{ color: BRAND_NAVY }}
          >
            {mobileOpen ? <X className="h-6 w-6" aria-hidden /> : <Menu className="h-6 w-6" aria-hidden />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 top-[81px] z-40 bg-slate-900/40 md:hidden"
          aria-label="Close menu"
          onClick={closeMobile}
          data-testid="mobile-site-nav-backdrop"
        />
      )}

      {mobileOpen && (
        <nav
          id={menuId}
          ref={menuRef}
          className="absolute left-0 right-0 top-full z-50 border-b border-slate-200 bg-white px-4 py-4 shadow-lg md:hidden"
          aria-label="Mobile"
          data-testid="mobile-site-nav-panel"
        >
          <ul className="space-y-1">
            <li>
              <Link to={createHref} className={mobileNavLinkClass(isCreateActive)} onClick={closeMobile}>
                Create Documents
              </Link>
            </li>
            <li>
              <Link
                to="/templates"
                className={mobileNavLinkClass(isTemplatesActive)}
                onClick={closeMobile}
                data-testid="mobile-nav-templates-toolkits"
              >
                Templates &amp; Toolkits
              </Link>
            </li>
            <li>
              <Link to="/contact" className={mobileNavLinkClass(isContactActive)} onClick={closeMobile}>
                Contact
              </Link>
            </li>
            {user ? (
              <>
                <li>
                  <Link to="/dashboard" className={mobileNavLinkClass(isDashboardActive)} onClick={closeMobile}>
                    Dashboard
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    className="flex min-h-12 w-full items-center rounded-xl px-4 text-left text-base font-medium text-slate-700 hover:bg-slate-50"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <li>
                <Link to="/login" className={mobileNavLinkClass(location.pathname === '/login')} onClick={closeMobile}>
                  Login
                </Link>
              </li>
            )}
          </ul>
          {!user && (
            <Link
              to="/signup"
              className="mt-4 flex min-h-12 w-full items-center justify-center rounded-2xl px-5 text-base font-semibold text-white shadow-sm transition hover:opacity-95"
              style={{ backgroundColor: BRAND_ORANGE }}
              onClick={closeMobile}
            >
              Get Started
            </Link>
          )}
        </nav>
      )}
    </header>
  );
}
